package dev.lingvo.shared.agent

import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.add
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray
import kotlinx.serialization.json.putJsonObject

/**
 * JSON Schema, written by hand and therefore written honestly.
 *
 * The two builders this replaces both guessed. The local one read a property's *name* — `dry_run`
 * and `obsolete_missing` were boolean, everything else string. The remote one declared every
 * property a required string, so an agent was told `entries` and `forms` were strings while the
 * handlers parsed them as objects, and told `project` was required when the server would have
 * resolved it.
 *
 * These functions cannot guess: a type is passed, or nothing is built.
 */
object AgentSchema {

    /** An object schema. `required` is spelled out; nothing is required by being present. */
    fun obj(vararg properties: Pair<String, JsonObject>, required: List<String> = emptyList()): JsonObject =
        buildJsonObject {
            put("type", "object")
            putJsonObject("properties") {
                properties.forEach { (name, schema) -> put(name, schema) }
            }
            putJsonArray("required") { required.forEach { add(it) } }
        }

    fun array(description: String, items: JsonObject): JsonObject = buildJsonObject {
        put("type", "array")
        put("description", description)
        put("items", items)
    }

    fun stringArray(description: String, values: List<String>? = null): JsonObject =
        array(description, string("Item.", values))

    fun obj(description: String, vararg properties: Pair<String, JsonObject>, required: List<String> = emptyList()): JsonObject =
        JsonObject(obj(*properties, required = required) + ("description" to JsonPrimitive(description)))

    fun string(description: String, values: List<String>? = null, default: String? = null): JsonObject =
        buildJsonObject {
            put("type", "string")
            put("description", description)
            values?.let { options -> putJsonArray("enum") { options.forEach { add(it) } } }
            default?.let { put("default", it) }
        }

    fun integer(
        description: String,
        minimum: Int? = null,
        maximum: Int? = null,
        default: Int? = null,
    ): JsonObject = buildJsonObject {
        put("type", "integer")
        put("description", description)
        minimum?.let { put("minimum", it) }
        maximum?.let { put("maximum", it) }
        default?.let { put("default", it) }
    }

    fun boolean(description: String, default: Boolean? = null): JsonObject = buildJsonObject {
        put("type", "boolean")
        put("description", description)
        default?.let { put("default", it) }
    }

    /**
     * A string-to-string map, which is what `entries` and `forms` have always actually been.
     *
     * `additionalProperties` rather than named properties: the keys are the customer's key names or
     * the locale's plural categories, and neither is knowable when the schema is written.
     */
    fun stringMap(description: String): JsonObject = buildJsonObject {
        put("type", "object")
        put("description", description)
        putJsonObject("additionalProperties") { put("type", "string") }
    }
}

/** The `type` a schema declares, or null when it declares none. */
val JsonObject.schemaType: String?
    get() = (this["type"] as? JsonPrimitive)?.content

/** The properties an object schema declares, in declaration order. */
val JsonObject.schemaProperties: Map<String, JsonObject>
    get() = (this["properties"] as? JsonObject)
        ?.mapValues { (_, value) -> value.jsonObject }
        .orEmpty()

/** The property names an object schema requires. */
val JsonObject.schemaRequired: List<String>
    get() = this["required"]?.jsonArray?.map { it.jsonPrimitive.content }.orEmpty()

/** A property's `description`, which every property in this catalogue has. */
val JsonObject.schemaDescription: String?
    get() = (this["description"] as? JsonPrimitive)?.content
